import React, { useState, useMemo, useRef } from 'react';
import { Box, IconButton, useMediaQuery } from '@mui/material';
import { 
  KeyboardDoubleArrowUp as ExpandIcon, 
  KeyboardDoubleArrowDown as CollapseIcon,
  StraightenOutlined as DistanceIcon,
  AccessTime as DurationIcon,
  Terrain as GradientIcon
} from '@mui/icons-material';
import { RoutePointsList } from '../routePointsList/RoutePointsList';
import { ElevationProfile } from '../elevationChart/ElevationProfile';
import { PowerDisplay } from '../powerDuration/PowerDisplay';
import { SurfaceProfile } from '../surfaceProfile/SurfaceProfile';
import { ClimbAnalysis } from '../climbAnalysis/ClimbAnalysis';
import { RouteDisclaimer } from '../disclaimer/RouteDisclaimer';
import { detectClimbs } from '../../utils/climbDetection';
import type { StatType } from '../statsBar/StatsBar';
import type { PointItem, ElevationPoint, RouteData } from '../../types';
import type { DropResult } from '@hello-pangea/dnd';
import './DetailPanel.css';

interface DetailPanelProps {
  activeStatType: StatType;
  points: PointItem[];
  elevationData: ElevationPoint[];
  routeData: RouteData;
  onPointDelete: (id: string) => void;
  onPointEdit?: (id: string, newPosition: { lat: number; lon: number }, displayName: string) => void;
  onPointInsert?: (insertIndex: number, newPosition: { lat: number; lon: number }, displayName: string) => void;
  onPointsReorder: (result: DropResult) => void;
  onManualGeocode?: (pointId: string, position: { lat: number; lon: number }) => void;
  geocodingLoadingPoints?: Set<string>;
  onHover: (index: number | null) => void;
  onClearStickyHover?: () => void;
  activeHoverPoint?: number | null;
  scrollToPoint?: string; // ID of point to scroll to
  deletingPointId?: string; // ID of point being deleted from external source (e.g., map)
  onSurfaceHighlight?: (surface: string | null, segmentIndex?: number) => void; // New callback for surface highlighting
  onClimbHighlight?: (climbIndex: number | null) => void; // New callback for climb highlighting
  onClearSurfaceHighlight?: () => void; // Callback to clear surface highlighting
  highlightedSurface?: string | null; // Currently highlighted surface from parent state
  highlightedClimbIndex?: number | null; // Currently highlighted climb from parent state
  isExpanded?: boolean; // Whether the panel is in expanded mode
  isDragging?: boolean; // Whether the panel is being dragged (to disable transitions)
  onExpandToggle?: () => void; // Callback to toggle expanded state
  routeSourceType?: 'manual' | 'gpx_import' | 'gpx_optimized'; // Route source type for geocoding decisions
  // Power calculator state from parent (single source of truth)
  calculatedDuration?: number | null;
  powerToWeight?: number;
  showOriginal?: boolean;
  averageSpeed?: number | null;
  speedChartData?: any[];
  onPowerToWeightChange?: (newValue: number) => void;
  onAutoModeChange?: (autoMode: boolean) => void;
}

export const DetailPanel: React.FC<DetailPanelProps> = ({
  activeStatType,
  points,
  elevationData,
  routeData,
  onPointDelete,
  onPointEdit,
  onPointInsert,
  onPointsReorder,
  onManualGeocode,
  geocodingLoadingPoints,
  onHover,
  onClearStickyHover,
  activeHoverPoint,
  scrollToPoint,
  deletingPointId,
  onSurfaceHighlight,
  onClimbHighlight,
  onClearSurfaceHighlight,
  highlightedSurface,
  highlightedClimbIndex,
  isExpanded = false,
  isDragging = false,
  onExpandToggle,
  routeSourceType,
  // Power calculator props from parent
  calculatedDuration,
  powerToWeight = 2.0,
  showOriginal = true,
  averageSpeed,
  speedChartData = [],
  onPowerToWeightChange,
  onAutoModeChange,
}) => {
 const [elevationHoverInfo, setElevationHoverInfo] = useState<{
    distance: number;
    time: string;
    gradient: number;
  } | null>(null);

 // Detect landscape orientation on mobile/tablet devices
 const isSmallScreen = useMediaQuery('(max-width: 900px)');
 const isShortScreen = useMediaQuery('(max-height: 600px)'); // Landscape mobile typically has limited height
 const isOrientationLandscape = useMediaQuery('(orientation: landscape)');
 const isLandscape = (isSmallScreen || isShortScreen) && isOrientationLandscape;

 // Use pre-calculated climbs from routeData (detected on full resolution data)
 // If not available, fall back to detecting from aggregated elevation data
 const climbs = useMemo(() => {
   if (routeData?.climbs && routeData.climbs.length > 0) {
     return routeData.climbs;
   }
   if (!elevationData || elevationData.length === 0) {
     return [];
   }
   return detectClimbs(elevationData);
 }, [routeData, elevationData]);

  if (!activeStatType) {
    return null;
  }

  // Common title component for consistent rendering
  const SectionTitle = ({ title, hoverInfo, showFullscreenButton, onFullscreenClick }: { 
    title: string; 
    hoverInfo?: { distance: number; time: string; gradient: number } | null;
    showFullscreenButton?: boolean;
    onFullscreenClick?: () => void;
  }) => {
    const isElevation = title === 'Elevation Profile';
    const isSmallScreen = window.innerWidth < 400;
    const gapSize = isSmallScreen ? '4px' : '8px';
    const metricTextStyle = {
      display: 'flex',
      alignItems: 'center',
      gap: '3px',
      fontSize: isSmallScreen ? '10px' : '12px',
      color: '#666',
      fontFamily: 'monospace',
      height: '20px',
      whiteSpace: 'nowrap'
    };

    const distanceDisplay = hoverInfo ? `${hoverInfo.distance.toFixed(1)}km` : '000.0km';
    const timeDisplay = hoverInfo?.time || '00:00';
    const gradientPrefix = hoverInfo && hoverInfo.gradient >= 0 ? '\u00A0' : '';
    const gradientDisplay = hoverInfo ? `${gradientPrefix}${hoverInfo.gradient.toFixed(1)}%` : '00.0%';
    const placeholderText = isSmallScreen ? 'Hover chart' : 'Hover over the diagram';

    return (
      <div 
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: title === 'Elevation Profile' ? '4px' : '4px',
          width: '100%',
          minHeight: '20px',
          paddingTop: '5px'
        }}
      >
        <div className="detail-panel-title" style={{ 
          flexShrink: 0,
          fontSize: isSmallScreen ? '0.65rem' : '0.75rem',
          minWidth: 'fit-content'
        }}>
          {title}
        </div>
        
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: gapSize,
          marginLeft: 'auto',
          minHeight: '20px'
        }}>
          {isElevation && (
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                height: '20px',
                minWidth: isSmallScreen ? '150px' : '210px'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isSmallScreen ? '6px' : '12px',
                  opacity: hoverInfo ? 1 : 0,
                  pointerEvents: 'none',
                  transition: 'opacity 120ms ease-out'
                }}
              >
                <div style={metricTextStyle}>
                  <DistanceIcon sx={{ fontSize: isSmallScreen ? '10px' : '12px', color: '#888' }} />
                  <span>{distanceDisplay}</span>
                </div>
                <div style={metricTextStyle}>
                  <DurationIcon sx={{ fontSize: isSmallScreen ? '10px' : '12px', color: '#888' }} />
                  <span>{timeDisplay}</span>
                </div>
                <div style={metricTextStyle}>
                  <GradientIcon sx={{ fontSize: isSmallScreen ? '10px' : '12px', color: '#888' }} />
                  <span>{gradientDisplay}</span>
                </div>
              </div>

              {!hoverInfo && (
                <span
                  style={{
                    position: 'absolute',
                    right: 0,
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: isSmallScreen ? '10px' : '12px',
                    color: '#ccc',
                    fontFamily: 'monospace',
                    lineHeight: '20px',
                    height: '20px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {placeholderText}
                </span>
              )}
            </div>
          )}

          {!isElevation && hoverInfo && (
            <div style={metricTextStyle}>
              <span>{hoverInfo.distance.toFixed(1)}km</span>
            </div>
          )}
          
          {showFullscreenButton && (
            <IconButton
              size="medium"
              onClick={onFullscreenClick}
              sx={{ 
                padding: isSmallScreen ? '2px' : '4px',
                color: 'text.secondary',
                flexShrink: 0,
                marginRight: '-10px', // Shift right to align better with scrollbar
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'primary.main'
                }
              }}
              title={isExpanded ? "Collapse panel" : "Expand panel"}
            >
              {isExpanded ? 
                <CollapseIcon sx={{ fontSize: isSmallScreen ? 16 : 20 }} /> :
                <ExpandIcon sx={{ fontSize: isSmallScreen ? 16 : 20 }} />
              }
            </IconButton>
          )}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeStatType) {
      case 'distance':
        return (
          <>
            <SectionTitle 
              title="Route Points" 
              showFullscreenButton={!isLandscape}
              onFullscreenClick={onExpandToggle}
            />
            <Box sx={{ 
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <RoutePointsList
                points={points}
                onPointDelete={onPointDelete}
                onPointEdit={onPointEdit}
                onPointInsert={onPointInsert}
                onPointsReorder={onPointsReorder}
                onManualGeocode={onManualGeocode}
                geocodingLoadingPoints={geocodingLoadingPoints}
                scrollToPoint={scrollToPoint}
                deletingPointId={deletingPointId}
                isExpanded={isExpanded}
                routeSourceType={routeSourceType}
              />
            </Box>
          </>
        );
      
      case 'ascend': {
        const noClimbs = climbs.length === 0;
        return (
          <>
            <SectionTitle 
              title="Elevation Profile" 
              hoverInfo={elevationHoverInfo} 
              showFullscreenButton={!isLandscape}
              onFullscreenClick={onExpandToggle}
            />
            <Box sx={{ 
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box 
                sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '3px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '3px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' } },
                  pb: noClimbs ? 1 : 0
                }}
              >
                <Box sx={{ flex: '0 0 auto', mb: noClimbs ? 1 : 0 }}>
                  <ElevationProfile
                    data={elevationData}
                    fullElevationData={routeData?.fullElevationData}
                    onHover={onHover}
                    variant="detail"
                    routeInfo={routeData ? { 
                      distance: routeData.distance, 
                      duration: routeData.duration, 
                      ascend: routeData.ascend,
                      coordinates: routeData.coordinates // Pass coordinates for accurate hover mapping
                    } : undefined}
                    onHoverInfoChange={setElevationHoverInfo}
                    stickyHover={true}
                    onClearStickyHover={onClearStickyHover}
                    activePoint={activeHoverPoint}
                    highlightedClimbIndex={highlightedClimbIndex}
                    climbs={climbs}
                  />
                </Box>
                <Box sx={{ flex: 1, minHeight: 0, mt: noClimbs ? 0.5 : 0 }}>
                  <ClimbAnalysis
                    elevationData={elevationData}
                    variant="detail"
                    onClimbClick={onClimbHighlight}
                    activeClimb={highlightedClimbIndex}
                    climbs={climbs}
                  />
                </Box>
              </Box>
            </Box>
          </>
        );
      }
      
      case 'duration':
        return (
          <>
            <SectionTitle 
              title="Duration & Speed" 
              showFullscreenButton={!isLandscape}
              onFullscreenClick={onExpandToggle}
            />
            <Box sx={{ 
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box 
                sx={{ 
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  px: 0.25, // 2px left and right padding
                  pr: 1, // 8px right padding for space between content and scrollbar
                  '&::-webkit-scrollbar': { width: '6px' },
                  '&::-webkit-scrollbar-track': { backgroundColor: 'rgba(0,0,0,0.05)', borderRadius: '3px' },
                  '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '3px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.3)' } }
                }}
              >
                <PowerDisplay 
                  routeData={routeData}
                  calculatedDuration={calculatedDuration ?? null}
                  powerToWeight={powerToWeight}
                  showOriginal={showOriginal}
                  averageSpeed={averageSpeed ?? null}
                  speedChartData={speedChartData}
                  onPowerToWeightChange={onPowerToWeightChange || (() => {})}
                  onAutoModeChange={onAutoModeChange || (() => {})}
                />
              </Box>
            </Box>
          </>
        );

      case 'surface':
        return (
          <>
            <SectionTitle 
              title="Surface Analysis" 
              showFullscreenButton={!isLandscape}
              onFullscreenClick={onExpandToggle}
            />
            <Box sx={{ 
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
            }}>
              <SurfaceProfile
                routeData={routeData}
                elevationData={elevationData}
                onHover={onHover}
                onSurfaceHighlight={onSurfaceHighlight}
                onClearSurfaceHighlight={onClearSurfaceHighlight}
                highlightedSurface={highlightedSurface}
              />
            </Box>
          </>
        );
      
      default:
        return null;
    }
  };

  const [isScrolling, setIsScrolling] = useState(false);
  const scrollEndTimerRef = useRef<number | null>(null);
  const lastTouchYRef = useRef<number | null>(null);

  // Calculate responsive heights based on expansion and orientation
  // In landscape: always full height when open (no collapsed state)
  // In portrait: use expanded or collapsed heights based on isExpanded
  const panelHeights = isLandscape ? {
    xs: 'calc(100% - 48px)', // Fill overlay space minus StatsBar height
    sm: 'calc(100% - 48px)'
  } : (isExpanded ? {
    xs: '55vh',
    sm: '60vh'
  } : {
    xs: '265px',
    sm: '285px'
  });

  const panelMinHeights = isLandscape ? {
    xs: 'calc(100% - 48px)', // Keep it consistent
    sm: 'calc(100% - 48px)'
  } : (isExpanded ? {
    xs: '400px',
    sm: '450px'
  } : {
    xs: '265px',
    sm: '285px'
  });

  const panelMaxHeights = isLandscape ? {
    xs: 'calc(100% - 48px)', // Match the height exactly
    sm: 'calc(100% - 48px)'
  } : (isExpanded ? {
    xs: '70vh',
    sm: '75vh'
  } : {
    xs: '265px',
    sm: '285px'
  });

  return (
    <Box 
      sx={{ 
        width: '100%',
        height: panelHeights,
        minHeight: panelMinHeights,
        maxHeight: panelMaxHeights,
        overflow: 'hidden',
        WebkitOverflowScrolling: 'touch', // Momentum scrolling on iOS
        overscrollBehavior: 'contain', // Prevent parent scroll chaining
        scrollBehavior: 'smooth',
        backgroundColor: 'background.paper',
        boxShadow: isExpanded ? 
          '0 -4px 16px rgba(0,0,0,0.15)' :
          '0 -2px 8px rgba(0,0,0,0.1)',
        px: 1,
        transition: isDragging ? 'none' : 'height 0.15s ease-out, min-height 0.15s ease-out, max-height 0.15s ease-out, box-shadow 0.15s ease-out',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        // Disable pointer events on charts when device is touch-only to improve scroll capture
        '& .elevation-chart-container': {
          pointerEvents: (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches && isScrolling)
            ? 'none'
            : 'auto',
          touchAction: 'pan-y'
        }
      }}
      onScroll={() => {
        if (!isScrolling) setIsScrolling(true);
        if (scrollEndTimerRef.current) window.clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = window.setTimeout(() => {
          setIsScrolling(false);
        }, 120);
      }}
      onTouchStart={(e) => {
        lastTouchYRef.current = e.touches[0]?.clientY ?? null;
      }}
      onTouchMove={(e) => {
        const currentY = e.touches[0]?.clientY ?? null;
        if (lastTouchYRef.current !== null && currentY !== null) {
          const deltaY = Math.abs(currentY - lastTouchYRef.current);
          if (deltaY > 4 && !isScrolling) {
            setIsScrolling(true);
          }
        }
      }}
      onTouchEnd={() => {
        lastTouchYRef.current = null;
        // Allow hover again shortly after touch ends
        window.setTimeout(() => setIsScrolling(false), 80);
      }}
      onTouchCancel={() => {
        lastTouchYRef.current = null;
        setIsScrolling(false);
      }}
    >
      {renderContent()}
      {/* Disclaimer at bottom of panel */}
      <RouteDisclaimer sx={{ 
        backgroundColor: 'background.paper',
        pt: 0.5,
        borderTop: '1px solid',
        borderColor: 'divider'
      }} />
      {/* Enable debug by executing in console: window.__DETAIL_PANEL_DEBUG = true */}
    </Box>
  );
};
